import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { QueuePanel } from './RightRail';
import type { QueueRow } from './RightRail';

/**
 * SAB-871: where the mobile action bar's queue button goes.
 *
 * MobileDraftRoom's action bar has always had an "Open queue" button, but the tab bar is
 * deliberately four items ("keeping the bar at four items") and the queue is not one of
 * them. A bottom sheet is the resolution: the queue is reachable in one tap without becoming
 * a fifth tab, and it closes back to whatever panel you were on rather than replacing it.
 *
 * The body is the DESKTOP QueuePanel, exported from RightRail rather than reimplemented --
 * reorder and remove are the same code paths, so the mobile queue cannot drift from the one
 * that actually drives auto-picks.
 */

export interface MobileQueueSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queue: QueueRow[];
  onQueueMove: (from: number, to: number) => void;
  onQueueRemove: (playerId: string) => void;
}

export default function MobileQueueSheet({
  open, onOpenChange, queue, onQueueMove, onQueueRemove,
}: MobileQueueSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="mobile-queue-sheet"
        className="flex max-h-[80dvh] flex-col gap-0 rounded-t-2xl p-0"
        // The sheet's own bottom edge has to clear the home indicator for the same reason
        // the action bar does -- the reorder controls sit at the bottom of the list.
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <SheetHeader className="flex-none border-b border-border px-4 py-3 text-left">
          <SheetTitle className="font-display text-sm font-bold uppercase tracking-[0.06em]">
            Your queue
          </SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueuePanel {...({ queue, onQueueMove, onQueueRemove } as unknown as Parameters<typeof QueuePanel>[0])} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
